import {CatalogStore, DragDropInteraction, site, ui} from '@stores'
import {CatalogInstanceChildStore} from '@stores/catalog/CatalogInstanceChildStore'
import {computedFn} from 'mobx-utils'
import {DEBUG_DRAGDROP, DragDropStore, DragStartOptions, IDragDropElement, IDragDropElementState, VerifyDragDropCallbackResult} from './'
import {action, computed, observable, runInAction, toJS, trace} from 'mobx'
import {_, React} from '@api'

/**
 * Generic drag start method for various draggable objects to use
 * @param id
 * @param source
 * @param type
 */
export class DragDropEventHandlers extends CatalogInstanceChildStore {
  @observable showDropIndicator = false

  constructor(public catalog: CatalogStore, public def: IDragDropElement) {
    super(catalog)

//    if (catalog.isGlobalStore) debugger
    const {dragDrop} = this

    //console.log(`Registered drag/drop handler for `, def)
    if (def.target === catalog.index && !catalog.isProductView) {
      // ID is full path to node including groups

      const {catalogItemId} = this
      if (dragDrop.ddHandlersCatalogItem[catalogItemId]) {
        this.state = {..._.first(dragDrop.ddHandlersCatalogItem[catalogItemId]).state}
        dragDrop.ddHandlersCatalogItem[catalogItemId].push(this)
      } else {
        dragDrop.ddHandlersCatalogItem[catalogItemId] = [this]
      }
    }

    setTimeout(() => {
      if (dragDrop.ddHandlers.has(def.id)) {
        // This isn't great that we have this happen, conflicts can be library item cards vs builder cards - same ID
        //console.warn(`dragDrop.ddHandlers already has a definition for ${def.id}`, dragDrop.ddHandlers.get(def.id).def)
        // TODO Probably should be id => Array<DragDropHandler> so they can all update loading and such, that or make id -> loading state and move it off the handler itself
      }
      dragDrop.ddHandlers.set(def.id, this)
    }, 0)
  }

  @computed get isDragging() {
    return this.def === this.dragDrop.dragItem
  }

  @computed get allowDrop() : VerifyDragDropCallbackResult {
    // console.log(`allowDrop? `, this.def.id)
    const {dragDrop} = this, r = dragDrop.allowDrop(this.def)
    if (!r.allowed) {
      DEBUG && console.debug(`Not Editable by the User`, toJS({drop: this.def, drag: dragDrop.dragItem}), r.message)
    }

    return r
  }

  @computed get isDropAllowed() {
    // console.log(`isDropAllowed? `, this.def.id)
    return this.isValidInteractionForCurrentDrag && this.showDropIndicator && this.allowDrop?.allowed
  }

  @action clearStatusDelayed = () => {
    setTimeout(() => {
      this.state.status = null
    }, ui.settings.updateIndicatorMs)
  }

  @computed get catalogItemId() {
    const {catalog} = this
    return catalog.isProductView ? this.def.id : catalog.parseIdFromPath(this.def.id)
  }

  dispose = () => {
    const {dragDrop: {ddHandlers, ddHandlersCatalogItem}, catalogItemId, catalog} = this

    ddHandlers.delete(this.def.id)

    if (catalog.isProductView && _.isArray(ddHandlersCatalogItem[catalogItemId])) {
      ddHandlersCatalogItem[catalogItemId] = ddHandlersCatalogItem[catalogItemId].filter(h => h != this)
      if (_.isEmpty(ddHandlersCatalogItem[catalogItemId])) {
        delete ddHandlersCatalogItem[catalogItemId]
      }
    }
  }

  get events() {
    const {onDrag, onDragEnd, onDragEnter, onDragLeave, onDragStart, onDrop, onDragOver} = this
    return {onDrag, onDragEnd, onDragEnter, onDragLeave, onDragStart, onDrop, onDragOver }
  }

  @observable state: IDragDropElementState = {}

  @computed get debugInfo() {
    const {dragDrop: {dragItem, dragOverItem}, def, state} = this
    return {from: dragItem.dragType, to: dragOverItem?.target, dragItem: dragItem?.id, dragOverItem: dragOverItem?.id, ...def, state}
  }

  // WARNING - do not make these @actions or else ...dragDropEventHandlers will not work correctly when hooking to react components
  onDrag = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
  }

  @computed get currentDragOverInteraction() : DragDropInteraction {
    const {flexLayout: {tabsByComponent}, dragDrop: {ddInteractions, dragType}, def: {target}} = this
    const r = ddInteractions[dragType] ? ddInteractions[dragType][target] : null

    // DEBUG_DRAGDROP && console.debug(`currentDragOverInteraction`, r)
    return r
  }

  @computed get isValidInteractionForCurrentDrag() {
    const {currentDragOverInteraction, flexLayout: {tabsByComponent}} = this

    const r = !!currentDragOverInteraction || !_.isEmpty(tabsByComponent['whiteboard'])

    if (!r) {
      DEBUG_DRAGDROP && console.debug(`Not a valid interaction`, {dragType: this.dragDrop.dragType, target: this.def.target})
      return false
    }

    if (currentDragOverInteraction?.verify) {
      const {allowed} = currentDragOverInteraction.verify(this.dragDrop.dragItem, this.def)
      return allowed
    }
    return r
  }

  /**
   * Allowed drag/drop interactions where this item is the drag type
   */
  @computed get allowedDropInteractionsWithThisAsDragItem() {
    return this.dragDrop.allowedDropInteractionsFor(this.def)
  }

  @computed get draggable() {
    return !_.isEmpty(this.allowedDropInteractionsWithThisAsDragItem)
  }

  onDragStart = (e: React.DragEvent, options?: DragStartOptions) => {
    const {def, def: {ignoreEvents, onDragStart}, draggable, dragDrop, catalog} = this

    if (ignoreEvents && ignoreEvents()) return

    // YFTS-7819 Don't allow users to begin a drag operation they can't do anything with
    if (!draggable) {
      e.preventDefault()
      e.stopPropagation()
    }
    else {
      dragDrop.DragDropItem_onDragStart(e, def, catalog, options)
    }

    onDragStart?.(e)

    //console.debug(`onDragStart`, this.def, this.debugInfo)
  }

  onDragEnd = (e: React.DragEvent) => {
    // console.debug(`onDragEnd`, this.debugInfo)
    this.dragDrop.reset(true)
  }

  /**
   * Called continuously as the user keeps dragging an item over our element or any of our children
   * @param e
   */
  onDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    this.dragDrop.updateDragImage(e)
    //e.stopPropagation()

    // dragDrop.updateDragImage(e)

    // console.log(`onDragOver`, e.currentTarget, this.debugInfo)

    return false
  }

  /**
   * We want to mark who we're dragging over
   * @param e
   */
  onDragEnter = (e?: React.DragEvent) => {
    if (!e) return

     // console.log(`onDragEnter`, e.currentTarget)

    if (!this.isValidInteractionForCurrentDrag || (this.def.ignoreEvents && this.def.ignoreEvents())) return false

    e.preventDefault()
    e.stopPropagation()

    const {def, def: { id }, dragDrop} = this, {dragOverItemElem} = dragDrop

    // console.log(`onDragEnter`, def.target, def.id, e.currentTarget, e.target)

    // console.log(`onDragEnter`, e.target, e.currentTarget, this.debugInfo)

    // if (this.def.dragType === dragDrop.dragItem.dragType && e.dataTransfer.files.length === 0) {
    //   // console.debug(`ignore dragEnter because dragged over item type === dragItem type`, this.def.dragType)
    //   return
    // }
    // console.debug(`onDragEnter`, this.debugInfo)

    if ((dragDrop.dragOverItem?.id != id && dragDrop.pendingDragOverItemId != id) || dragOverItemElem != e.currentTarget) {
      // console.debug(`Set drag over item to `, this.def.target, this.def.id)
      //runInAction(`Set drag over item - '${def.id}' (${def.target ?? def.dragType})`, () => {
      //   console.log(`Update dragOverItem to ${def.id}`)
      dragDrop.pendingDragOverItemId = id

      if (!dragDrop.checkedForImageInDataTransfer) {
        const {dragItem} = dragDrop
        if (dragItem.dragType != 'images' && e.dataTransfer.types.findIndex((a) => a === 'Files') > -1) {
          dragDrop.dragItem.dragType = 'images'
        }
        dragDrop.checkedForImageInDataTransfer = true
      }
        // console.log(e.dataTransfer.types, e.dataTransfer.files, e.dataTransfer.items, e.dataTransfer.items, e.dataTransfer.getData('text/plain'), toJS(dragItem))
        // })

      // dragDrop.setDragOverItem(def, e.currentTarget)
      dragDrop.setDragOverItem_debounce(def, e.currentTarget)

      if (dragDrop.dragItem.id == null) {
        /**
         * For External Drag Item, onDragEnter will not get id in dropDrop.dragItem.
         * read the dataTransfer and set the EXTERNAL Id to the dragDrop.dragItem
         **/
        console.log(`No drag item is set - reading data transfer to determine dragging item from outside source`, toJS(dragDrop.dragItem))
        dragDrop.readDataTransfer(e.dataTransfer)
      }
    }

    return false
  }

  onDragLeave = (e: React.DragEvent) => {
    if (!this.isValidInteractionForCurrentDrag || (this.def.ignoreEvents && this.def.ignoreEvents())) return

    e.preventDefault()
    e.stopPropagation()

    // console.log(`onDragLeave`, this.def.id, e.currentTarget, e.target)
    // console.debug(`onDragLeave`, e.target, e.currentTarget, this.debugInfo)

    const {dragDrop, def} = this,
      {isDragging, dragOverItem, dragOverItemElem} = dragDrop
    /*if (isDragging) {
      if (dragOverItem && (!def || dragOverItem.id == def.id)) {
        dragDrop.dragOverItem = null
      }
    }*/

    // Use pointer-events: none on children to prevent invalid drag leaves - this code is too expensive to be firing constantly
    if (isDragging && dragOverItem && dragOverItem.id == def.id && dragOverItemElem === e.currentTarget) {
      // DEBUG && console.log(`onDragLeave - Clearing drag over item`, def.target, def.id, e.target, e.currentTarget, e.relatedTarget)
      dragDrop.setDragOverItem(null)

      // const {relatedTarget, currentTarget} = e
      //
      // let hasLeftElement = false
      // // Chrome and Firefox
      // if (relatedTarget) {
      //   hasLeftElement = !currentTarget.contains(relatedTarget as Node)
      // } else if (utils.isSafari) {
      //   // NOTE: relatedTarget is not supported in Safari yet (https://bugs.webkit.org/show_bug.cgi?id=66547)
      //   // and therefore, had to do below hack to verify if mouse pointer has moved out of currentTarget
      //   // Expensive!
      //   let rect = currentTarget.getBoundingClientRect()
      //   hasLeftElement = e.clientX >= (rect.left + rect.width) || e.clientX <= rect.left || e.clientY >= (rect.top + rect.height) || e.clientY <= rect.top
      // }
      // if (hasLeftElement) {
      //   if (!def || dragDrop.dragOverItem.id == def.id) {
      //     asyncClearDragOverItem()
      //   }
      // }
    }

    return false
  }

  onDrop = (e: React.DragEvent) => {
    if (this.def.ignoreEvents && this.def.ignoreEvents()) return

    this.dragDrop._handleDrop(e, this.def)
  }
}




// WEBPACK FOOTER //
// ./src/stores/dragDrop/DragDropItemStore.ts